<template>
  <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
    <div class="mt-6">
      <div class="flex items-center mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Return History</h3>
        <hr class="flex-1 border-primary ml-4" />
      </div>

      <div v-if="isBusy" class="flex items-center justify-center py-4">
        <spinner :color="'text-black'" :size="6" />
      </div>

      <p v-else-if="message" class="text-sm text-gray-600" data-testid="timeline-message">{{ message }}</p>

      <Timeline v-else :value="steps" align="left" class="return-timeline" dataKey="key">
        <template #opposite="slotProps">
          <p class="text-sm text-gray-600 whitespace-no-wrap" :data-testid="'timeline-when-' + slotProps.item.key">
            {{ formatWhen(slotProps.item.at) }}
          </p>
        </template>
        <template #marker="slotProps">
          <span class="return-timeline-marker" :class="markerClass(slotProps.item.key)"></span>
        </template>
        <template #content="slotProps">
          <p class="font-semibold text-secondary" :data-testid="'timeline-title-' + slotProps.item.key">
            <router-link
              v-if="linkFor(slotProps.item)"
              :to="linkFor(slotProps.item)"
              class="text-blue-700 hover:underline"
              :data-testid="'timeline-link-' + slotProps.item.key"
            >
              {{ slotProps.item.title }}
            </router-link>
            <template v-else>{{ slotProps.item.title }}</template>
          </p>
          <p v-if="slotProps.item.detail" class="text-sm">{{ slotProps.item.detail }}</p>
          <p v-if="slotProps.item.by" class="text-xs text-gray-600">by {{ slotProps.item.by }}</p>
        </template>
      </Timeline>
    </div>
  </div>
</template>

<script>
import Spinner from "@/components/Spinner.vue";

const MARKER_CLASSES = {
  ticket: "bg-orange-400",
  form_sent: "bg-blue-400",
  form_submitted: "bg-blue-400",
  return_created: "bg-blue-400",
  label_issued: "bg-gray-400",
  received: "bg-green-400",
  confirmed: "bg-green-400",
};

export default {
  name: "ReturnTimeline",
  components: {
    Spinner,
  },
  props: {
    returnNo: {
      type: String,
      default: "",
    },
    hideTicketStep: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isBusy: false,
      steps: [],
      message: "",
    };
  },
  watch: {
    returnNo: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
  methods: {
    async load() {
      const returnNo = String(this.returnNo || "").trim();
      this.steps = [];
      this.message = "";

      if (!returnNo) {
        this.message = "No return is linked to this record.";
        return;
      }

      try {
        this.isBusy = true;
        const timeline = await this.ReturnService.getTimeline(returnNo);
        this.steps = (timeline.steps || []).filter((step) => !(this.hideTicketStep && step.key === "ticket"));
        if (this.steps.length === 0) this.message = "Nothing has happened on this return yet.";
      } catch (error) {
        this.message = `No history found for return ${returnNo}.`;
      } finally {
        this.isBusy = false;
      }
    },
    formatWhen(unix) {
      if (!unix) return "";
      return this.$moment.unix(unix).format("DD/MM/YYYY HH:mm");
    },
    markerClass(key) {
      return MARKER_CLASSES[key] || "bg-gray-400";
    },
    linkFor(step) {
      const current = this.$route ? this.$route.params || {} : {};

      if (step.ticket_id && current.ticketId !== step.ticket_id) {
        return { name: "service-ticket-edit", params: { ticketId: step.ticket_id } };
      }
      if (step.return_id && current.returnId !== step.return_id) {
        return { name: "return-edit", params: { returnId: step.return_id } };
      }
      if (step.goods_in_id && current.goods_in_id !== step.goods_in_id) {
        return { name: "goods-in-edit", params: { goods_in_id: step.goods_in_id } };
      }
      return null;
    },
  },
};
</script>

<style scoped>
.return-timeline-marker {
  display: block;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 2px #cbd5e0;
}

.return-timeline >>> .p-timeline-event-opposite {
  flex: 0 0 9rem;
  text-align: right;
  padding-right: 1rem;
}

.return-timeline >>> .p-timeline-event-content {
  padding-bottom: 1.25rem;
}
</style>
